import React, { useContext, useState } from 'react'
import { Card, Button, Form, Row, Col, Container } from 'react-bootstrap'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { AuthContext } from '../../AuthContext'
import Cookies from 'js-cookie'

// 定義情緒類型選項，包含顯示標籤和對應的值
const EMOTION_TYPES = [
  { value: 0, label: '很棒' },
  { value: 1, label: '開心' },
  { value: 2, label: '還行' },
  { value: 3, label: '不開心' },
  { value: 4, label: '很糟' },
]

const AddEmotion = ({ onSuccess, onCancel, date }) => {
  const { patient_id } = useParams()
  const { baseUrl, currentUser, extendCookie, setLoginShow } =
    useContext(AuthContext)
  const sessionFromCookie = Cookies.get('session')
  const { session } = JSON.parse(sessionFromCookie || '{}')

  // 初始化表單狀態
  const [records, setRecords] = useState([
    {
      time: '',
      emotion_type: '',
      note: '',
      comment: '',
    },
  ])

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  // 添加新的一筆記錄欄位
  const handleAddNewRow = () => {
    setRecords((prev) => [
      ...prev,
      {
        time: '',
        emotion_type: '',
        note: '',
        comment: '',
      },
    ])
  }

  // 處理輸入變更
  const handleInputChange = (index, field, value) => {
    setRecords((prev) =>
      prev.map((record, i) =>
        i === index ? { ...record, [field]: value } : record
      )
    )

    // 清除該欄位的錯誤訊息
    if (errors[`${field}_${index}`]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[`${field}_${index}`]
        return newErrors
      })
    }
  }

  // 驗證表單
  const validateForm = () => {
    const newErrors = {}
    let isValid = true

    records.forEach((record, index) => {
      // 檢查時間
      if (!record.time) {
        newErrors[`time_${index}`] = '請輸入時間'
        isValid = false
      }

      // 檢查情緒類型
      if (record.emotion_type === '') {
        newErrors[`emotion_type_${index}`] = '請選擇心情'
        isValid = false
      }
    })

    setErrors(newErrors)
    return isValid
  }

  // 提交新記錄
  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setIsSubmitting(true)

      // 格式化數據
      const emotion = records.map((record) => ({
        emotion_type: parseInt(record.emotion_type),
        note: record.note.trim(),
        comment: record.comment.trim() || '',
        record_time: `${date} ${record.time}:00`,
      }))

      const response = await axios.post(
        `${baseUrl}/api/crm/patient/measure/addEmotion`,
        {
          patient_id,
          emotion,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Accept: '*/*',
            session: currentUser?.session || session || '',
          },
        }
      )

      if (response.data.message === '新增成功') {
        extendCookie()
        onSuccess?.()
      }
    } catch (error) {
      console.error('Error adding emotion records:', error)
      if (error.response?.data?.error?.code === '50004') {
        setLoginShow(true)
      } else {
        setErrors({ submit: '新增記錄失敗，請稍後再試' })
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Container className="py-4">
      <div className="mb-4">
        <Button
          variant="link"
          onClick={onCancel}
          className="text-decoration-none text-secondary"
        >
          <h5>
            <i className="fa-solid fa-arrow-left me-2" />
            返回數據收集紀錄
          </h5>
        </Button>
      </div>

      <Card className="position-relative mb-4 rounded-3 p-4">
        {/* 日期和按鈕區域 */}
        <div className="d-flex justify-content-between align-items-center">
          <h4 className="m-0">{date}</h4>
          <div>
            <Button
              variant="outline-primary"
              className="me-2"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              取消
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? '儲存中...' : '儲存'}
            </Button>
          </div>
        </div>

        {/* 記錄輸入區域 */}
        {records.map((record, index) => (
          <div key={index} className="mt-4 p-3">
            <Row className="g-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Control
                    type="time"
                    value={record.time}
                    onChange={(e) =>
                      handleInputChange(index, 'time', e.target.value)
                    }
                    isInvalid={!!errors[`time_${index}`]}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors[`time_${index}`]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Select
                    value={record.emotion_type}
                    onChange={(e) =>
                      handleInputChange(index, 'emotion_type', e.target.value)
                    }
                    isInvalid={!!errors[`emotion_type_${index}`]}
                  >
                    <option value="">選擇心情</option>
                    {EMOTION_TYPES.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Control.Feedback type="invalid">
                    {errors[`emotion_type_${index}`]}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Control
                  placeholder="備註可留空"
                  value={record.comment}
                  onChange={(e) =>
                    handleInputChange(index, 'comment', e.target.value)
                  }
                />
              </Col>
            </Row>
            <Row className="mt-3">
              <Col>
                <Form.Group>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    placeholder="心情日記(選填)"
                    value={record.note}
                    onChange={(e) =>
                      handleInputChange(index, 'note', e.target.value)
                    }
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>
        ))}

        {/* 新增一筆按鈕 */}
        <div className="d-flex justify-content-center mt-3">
          <Button
            variant="primary"
            className="text-white text-decoration-none"
            onClick={handleAddNewRow}
            disabled={isSubmitting}
          >
            + 新增一筆
          </Button>
        </div>
      </Card>

      {errors.submit && (
        <div className="text-danger text-center mt-2">{errors.submit}</div>
      )}
    </Container>
  )
}

export default AddEmotion
